import { get, post } from "@/shared/utils/fetchApi";

/**
 * Interface para solicitação de recuperação de senha
 */
export interface PasswordResetRequestDTO {
  email: string;
  locale?: string;
}

/**
 * Interface unificada para a resposta da validação do token de reset de senha
 */
export interface TokenValidationResponse {
  success: boolean;
  message: string;
  data?: {
    userId?: string;
    token?: string;
    [key: string]: unknown;
  };
}

/**
 * Interface para redefinição de senha
 */
export interface PasswordResetDTO {
  userId?: string; // Opcional, pois o token JWT pode conter o userId
  token: string;
  password: string;
}

/**
 * Classe de serviço para encapsular operações de recuperação de senha
 */
export class PasswordResetService {
  /**
   * Solicita o envio de um email com o link para recuperação de senha
   * @param data Dados para solicitação de reset de senha
   * @returns Objeto com status do sucesso e mensagem
   */
  static async requestReset(
    data: PasswordResetRequestDTO,
  ): Promise<{ success: boolean; message: string }> {
    try {
      // v2 retorna `{message}` flat (sem `success`) → normaliza.
      const res = await post<{ message: string }>(
        "/auth/password-reset/request",
        data,
      );
      return { success: true, message: res?.message ?? "" };
    } catch (error: unknown) {
      console.error("Erro ao solicitar recuperação de senha:", error);

      // Normaliza em vez de devolver `response.data` cru. O corpo de erro da api
      // não tem `success` (o `fetchApi` guarda ali o payload como veio, e num 429
      // ele nem traz `message`), então repassá-lo entregava `success: undefined`
      // a quem espera boolean — falso por acidente, e sem mensagem.
      const apiError = error as {
        response?: { data?: { message?: string } };
      };
      const serverMessage = apiError.response?.data?.message;

      return {
        success: false,
        message:
          serverMessage ||
          (error instanceof Error ? error.message : undefined) ||
          "Erro ao solicitar recuperação de senha",
      };
    }
  }

  /**
   * Valida o token de recuperação de senha
   * @param token Token de recuperação JWT recebido por email
   * @returns Objeto com status do sucesso, mensagem e dados do token
   */
  static async validateToken(
    token: string,
  ): Promise<{
    success: boolean;
    message: string;
    data?: Record<string, unknown>;
    code?: string;
  }> {
    // v2 (fluxo legacy portado): valida o JWT + match no DB. Retorna `{userId, token}`
    // flat → normaliza pra `.data` (o componente lê `tokenValidation.data.userId`).
    try {
      const data = await get<{ userId: string; token: string }>(
        `/auth/password-reset/validate/${token}`,
      );
      return {
        success: true,
        message: "",
        data: { userId: data?.userId, token: data?.token },
      };
    } catch (error: unknown) {
      const apiError = error as {
        data?: { message?: string; code?: string };
        message?: string;
        code?: string;
      };
      return {
        success: false,
        message:
          apiError.data?.message ||
          apiError.message ||
          "Token inválido ou expirado",
        code: apiError.data?.code || apiError.code,
      };
    }
  }

  /**
   * Redefine a senha do usuário usando o token JWT validado
   * @param data Dados para redefinição de senha (token JWT, userId opcional e nova senha)
   * @returns Objeto com status do sucesso e mensagem
   */
  static async resetPassword(
    data: PasswordResetDTO,
  ): Promise<{
    success: boolean;
    message: string;
    code?: string;
    data?: Record<string, unknown>;
  }> {
    try {
      // v2 (fluxo legacy portado): {userId, token, password} → /auth/password-reset/reset.
      const response = await post<{ userId: string }>(
        "/auth/password-reset/reset",
        {
          userId: data.userId,
          token: data.token,
          password: data.password,
        },
      );
      return {
        success: true,
        message: "Senha redefinida com sucesso",
        data: (response ?? undefined) as Record<string, unknown> | undefined,
      };
    } catch (error: unknown) {
      console.error("[PasswordResetService] Erro ao redefinir senha:", error);
      const apiError = error as {
        message?: string;
        status?: number;
        code?: string;
        name?: string;
        response?: unknown;
      };
      console.error("[PasswordResetService] Detalhes do erro:", {
        message: apiError.message,
        status: apiError.status,
        code: apiError.code,
        name: apiError.name,
        response: apiError.response ? "present" : "absent",
      });

      return {
        success: false,
        message: apiError.message || "Erro ao redefinir senha",
        code: apiError.code,
      };
    }
  }
}
